import {
  Button,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@repo/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Info, Pencil, Truck, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { AdminPageHeader } from '@/features/admin/layout/admin-page-header'
import { OrganizationDetailsPanel } from '@/features/admin/organizations/components/organization-details-panel'
import { OrganizationPlanPanel } from '@/features/admin/organizations/components/organization-plan-panel'
import { OrganizationRetentionPanel } from '@/features/admin/organizations/components/organization-retention-panel'
import { OrganizationMembersTable } from '@/features/admin/organizations/components/organization-members-table'
import { OrganizationVehiclesTable } from '@/features/admin/organizations/components/organization-vehicles-table'
import { useAdminOrganization } from '@/features/admin/organizations/hooks/use-admin-organization'
import { useAdminOrganizationMembers } from '@/features/admin/organizations/hooks/use-admin-organization-members'
import { useAdminOrganizationVehicles } from '@/features/admin/organizations/hooks/use-admin-organization-vehicles'

export const Route = createFileRoute('/admin/organizations/$orgId/')({
  component: AdminOrganizationDetailPage,
})

function AdminOrganizationDetailPage() {
  const { t } = useTranslation('admin')
  const { orgId } = Route.useParams()
  const id = Number(orgId)

  const { data: org, isPending } = useAdminOrganization(id)
  const { data: members, isPending: membersPending } =
    useAdminOrganizationMembers(id)
  const { data: vehicles, isPending: vehiclesPending } =
    useAdminOrganizationVehicles(id)

  return (
    <div className="space-y-4">
      <AdminPageHeader
        title={org?.name ?? t('organizations.detail.title')}
        subtitle={org?.timezone}
        ctaButton={
          <Button asChild variant="outline">
            <Link
              to="/admin/organizations/$orgId/edit"
              params={{ orgId: String(id) }}
            >
              <Pencil className="size-4" />
              {t('organizations.list.edit')}
            </Link>
          </Button>
        }
      />

      {isPending || !org ? (
        <Skeleton className="h-96 w-full rounded-md" />
      ) : (
        <Tabs defaultValue="details">
          <TabsList>
            <TabsTrigger value="details">
              <Info className="size-4" />
              {t('organizations.detail.tabs.details')}
            </TabsTrigger>
            <TabsTrigger value="members">
              <Users className="size-4" />
              {t('organizations.detail.tabs.members')}
              {members && (
                <span className="text-muted-foreground ml-1 text-xs">
                  ({members.length})
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="vehicles">
              <Truck className="size-4" />
              {t('organizations.detail.tabs.vehicles')}
              {vehicles && (
                <span className="text-muted-foreground ml-1 text-xs">
                  ({vehicles.length})
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-4 space-y-4">
            <OrganizationDetailsPanel organization={org} />
            <OrganizationPlanPanel organization={org} />
            <OrganizationRetentionPanel organizationId={id} />
          </TabsContent>

          <TabsContent value="members" className="mt-4">
            <OrganizationMembersTable
              members={members}
              isLoading={membersPending}
            />
          </TabsContent>

          <TabsContent value="vehicles" className="mt-4">
            <OrganizationVehiclesTable
              vehicles={vehicles}
              isLoading={vehiclesPending}
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  )
}
